import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { configData } from '../config';
import Loading from './Loading';
import {
  GLOBAL_CONTENT_FIELDS,
  GLOBAL_CONTENT_GROUPS,
  applyGlobalValues,
} from './PageEditor/globalContentStore';

// ============================================================
// GLOBAL COURSE CONTENT — admin editor (administrator only)
// ============================================================
// Lists every currently-shared course-wide dynamic field (grouped by the
// component that uses it) with an editable value. Each value is the SINGLE
// source of truth: saving here writes the `global_course_content` table via
// the Yii2 backend, and every course page resolves the new value dynamically
// (through globalContentStore) with no change to any course page's saved JSON.
//
// The field list is NOT hardcoded here — it comes from GLOBAL_CONTENT_FIELDS,
// the same registry the runtime resolvers use, so the editor and the live site
// can never list a different set of fields.
//
// Access is administrator-only on the route (ProtectedRoute roles=['administrator'])
// AND enforced server-side: pages/saveglobalcontent returns 403 for anyone who
// is not an administrator (SAAdmin included).

const app_url = configData.APP_URL;

// Build the initial editable state from the registry fallbacks, so the form is
// always populated even before the backend responds.
const buildInitialValues = () =>
  GLOBAL_CONTENT_FIELDS.reduce((m, f) => { m[f.key] = f.fallback; return m; }, {});

const GlobalCourseContent = () => {
  const [values, setValues] = useState(buildInitialValues);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Load current values from the backend on mount. On failure we keep the
  // registry fallbacks already in state and surface the reason.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setIsLoading(true);
        setErrorMessage('');
        const res = await fetch(app_url + 'pages/getglobalcontent', {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json().catch(() => ({}));
        const map = (data && (data.data || data)) || {};
        if (cancelled) return;
        // Only overwrite fields we know about, and only with real strings —
        // an empty stored value falls back to the registry default.
        setValues((prev) => {
          const next = { ...prev };
          GLOBAL_CONTENT_FIELDS.forEach((f) => {
            const v = map[f.key];
            if (typeof v === 'string' && v.trim() !== '') next[f.key] = v;
          });
          return next;
        });
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(
          'Could not load saved values from `pages/getglobalcontent` (' +
          ((err && err.message) || 'request failed') +
          '). Showing default values — you can still edit and save.'
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const handleChange = useCallback((key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setSuccessMessage('');
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    // Validate: every global field must be non-empty. A blank value here would
    // wipe a heading/label site-wide, so it is rejected before saving.
    const trimmed = {};
    for (const f of GLOBAL_CONTENT_FIELDS) {
      const v = (values[f.key] == null ? '' : String(values[f.key])).trim();
      if (v === '') {
        setErrorMessage(`"${f.group} → ${f.label}" cannot be empty. Every global value is used site-wide.`);
        return;
      }
      trimmed[f.key] = v;
    }

    try {
      setIsSaving(true);
      const res = await fetch(app_url + 'pages/saveglobalcontent', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ values: trimmed }),
      });
      if (res.status === 403) {
        throw new Error('You do not have permission to change Global Course Content (administrator only).');
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok || (data && data.code && data.code !== 0)) {
        throw new Error((data && data.message) || ('HTTP ' + res.status));
      }
      // Reflect the saved values in the form and push them into the live store
      // so this admin's own open course pages update immediately.
      const saved = (data && (data.data || data)) || trimmed;
      const nextValues = {};
      GLOBAL_CONTENT_FIELDS.forEach((f) => {
        const v = saved[f.key];
        nextValues[f.key] = (typeof v === 'string' && v.trim() !== '') ? v : trimmed[f.key];
      });
      setValues((prev) => ({ ...prev, ...nextValues }));
      applyGlobalValues(nextValues);
      setSuccessMessage('Global Course Content saved. Course pages now use these values.');
      // Return the admin to the top of the page so the success banner (rendered
      // above the form) is visible after saving from anywhere down the form.
      if (typeof window !== 'undefined' && window.scrollTo) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      setErrorMessage('Save failed: ' + ((err && err.message) || 'unknown error') + '. Nothing was changed.');
    } finally {
      setIsSaving(false);
    }
  }, [values]);

  if (isLoading) return <Loading />;

  return (
    <div id="main">
      <div className="left"></div>
      <div className="center left-center">
        <div className="manage-blog-container" style={{ maxWidth: 720 }}>
          <Helmet>
            <title>Global Course Content</title>
          </Helmet>

          <h1 style={{ marginBottom: 4 }}>Global Course Content</h1>
          <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0 }}>
            Shared text used across every course page. Each value is the single source of
            truth — a change here updates that text everywhere the component appears, without
            touching any individual course page. Course-specific content (bullets, links,
            schedules) is not shown here and is unaffected.
          </p>

          {successMessage && (
            <div className="set-message-done">
              <span className="cart-message-icon message-icon">
                <i className="fa fa-check-circle"></i>
              </span>{' '}
              {successMessage}
            </div>
          )}
          {errorMessage && (
            <div
              style={{
                background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e',
                padding: '10px 12px', borderRadius: 6, margin: '12px 0', fontSize: 13,
              }}
            >
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {GLOBAL_CONTENT_GROUPS.map((grp) => (
              <fieldset
                key={grp.group}
                style={{
                  border: '1px solid #e5e7eb', borderRadius: 8,
                  padding: '12px 16px 16px', margin: '16px 0',
                }}
              >
                <legend style={{ fontWeight: 700, fontSize: 14, padding: '0 6px' }}>
                  {grp.group}
                </legend>
                {grp.fields.map((f) => (
                  <div key={f.key} style={{ marginBottom: 14 }}>
                    <label
                      htmlFor={'gcc-' + f.key}
                      style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4 }}
                    >
                      {f.label}
                    </label>
                    {f.multiline ? (
                      <textarea
                        id={'gcc-' + f.key}
                        value={values[f.key] == null ? '' : values[f.key]}
                        onChange={(e) => handleChange(f.key, e.target.value)}
                        maxLength={255}
                        rows={2}
                        style={{
                          width: '100%', boxSizing: 'border-box', padding: '8px 10px',
                          border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14,
                          resize: 'vertical', fontFamily: 'inherit',
                        }}
                      />
                    ) : (
                      <input
                        id={'gcc-' + f.key}
                        type="text"
                        value={values[f.key] == null ? '' : values[f.key]}
                        onChange={(e) => handleChange(f.key, e.target.value)}
                        maxLength={255}
                        style={{
                          width: '100%', boxSizing: 'border-box', padding: '8px 10px',
                          border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14,
                        }}
                      />
                    )}
                    {f.hint && (
                      <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 4 }}>{f.hint}</div>
                    )}
                  </div>
                ))}
              </fieldset>
            ))}

            <button
              type="submit"
              className="form-submit"
              disabled={isSaving}
              style={{ opacity: isSaving ? 0.6 : 1 }}
            >
              {isSaving ? 'Saving…' : 'Save Global Content'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GlobalCourseContent;
